import { useDispatch, useSelector } from 'react-redux';
import { NewTabLink } from 'ui/links';
import { useEffect, useState, useId } from 'react';
import { selectFeatureFlags, selectOrganizationId } from 'core/userContext';
import { ModalFooter, ModalBody, ModalHeader, Modal, MODAL_SIZES } from 'ui/modal';
import { Container } from 'ui/container';
import { Body2, BoldText, Caption } from 'ui/theme/typography';
import { FormField } from 'ui/formField';
import { RangeSlider } from 'ui/rangeSlider/RangeSlider';
import { PrimaryButton } from 'ui/button';
import { CaptionContainer } from './OrganizationSyncEditModal.styled';
import { fetchHightouchAllotmentRequest, updateHightouchAllotmentRequest } from '../actions';
import { getHightouchAllotment } from '../selectors';
import type { OrganizationSyncEditModalProps } from '../types';

export const OrganizationSyncEditModal = ({ onClose }: OrganizationSyncEditModalProps) => {
  const id = useId();
  const formId = useId();
  const dispatch = useDispatch();
  const { HightouchIntegration } = useSelector(selectFeatureFlags);
  const allotment = useSelector(getHightouchAllotment);
  const orgId = useSelector(selectOrganizationId);

  const hightouchIsEnabled = !!HightouchIntegration;
  const [syncs, setSyncs] = useState(allotment);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(updateHightouchAllotmentRequest(syncs));
    onClose();
  };

  useEffect(() => {
    dispatch(fetchHightouchAllotmentRequest());
  }, [dispatch]);

  useEffect(() => {
    setSyncs(allotment);
  }, [allotment]);

  return (
    <Modal onClose={onClose} isModalOpen id={id} size={MODAL_SIZES.SMALL}>
      <ModalHeader>Update Smart Ingest limit</ModalHeader>
      <ModalBody>
        <Body2>
          Set the Smart Ingest limit for this organization <BoldText>(Org ID: {orgId})</BoldText>.
        </Body2>
        <Body2>
          To update another organization&apos;s limit, impersonate a user from that organization and
          return to this page.
        </Body2>
        <Container size="medium" variant="secondary">
          <form onSubmit={onSubmit} id={formId}>
            <FormField labelText="Smart Ingest Sync Limit">
              <RangeSlider
                disabled={!hightouchIsEnabled}
                min={5}
                max={25}
                step={1}
                value={[syncs]}
                onValueChange={([newVal]) => {
                  setSyncs(newVal);
                }}
              />
            </FormField>
          </form>
        </Container>
        <CaptionContainer>
          <Caption>
            If the organization is testing syncs, apply the HightouchTestProject to the project
            instead of increasing the limit.
          </Caption>
          <Caption>
            For more details, review the&nbsp;
            <NewTabLink
              href="https://support.iterable.com/hc/en-us/articles/24354008224916-Smart-Ingest-Overview"
              size="caption"
              useReactRouter={false}
            >
              Smart Ingest documentation
            </NewTabLink>
          </Caption>
        </CaptionContainer>
      </ModalBody>
      <ModalFooter onClose={onClose}>
        <PrimaryButton type="submit" form={formId}>
          Update
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
